import {
  ServiceTypeResponse,
  StructureLayoutData,
  StructureTypeResponse,
  SiteSchemaDropdown,
  StructureAttribute,
  StructureMergeLocationNodeData,
  Warning,
  ServiceStatusTransitionsType,
  ServiceTypeMappingType,
  RegionOverridesType,
} from '../@types';
import { isDropDownEmpty, isEmptyString } from './common';
import { StructureAdditionalConfigOptionType } from '../@types/structureAdditionalConfig.type';
import { DropdownItem } from '../components/atoms/MultiSelectCheckmarksMenu';
import { TradingHoursData } from '../components/molecules/TradingHours';
import { CapacityData } from '../components/organisms/Capacity';
import { AddressDataType } from '../components/organisms/StructureAddress';
import {
  ContactDataType,
  EmptyMainAddress,
  EmptyMainContact,
} from '../components/templates/StructureAddressContactLayout';
import { PropertyType as PropertyTypeEnum, SpecialServiceTasksEnum } from '../configs/enums';
import { REACT_APP_CVTS_CLIENT_NODE_ID, REACT_APP_CVTS_CONTRACT_NODE_ID } from '../configs/envs';
import { ANY_KEYWORD, EMPTY_FIELD_MESSAGE } from '../constants/common';
import { SERVICE_PRESENTATION_OPTIONS } from '../constants/interaction.constant';

export const getServiceTypesDropdownData = (serviceTypes: Array<ServiceTypeResponse>) => {
  return serviceTypes.map((serviceType) => {
    return {
      value: serviceType?.code,
      label: serviceType?.name,
    };
  });
};

export const getSortedStructureTypes = (
  structureTypesData: Array<StructureTypeResponse>,
  preferedNodeTypes: Array<string>,
  allowedNodeTypes?: string[],
  isBrandParent?: boolean
) => {
  let data = structureTypesData
    .filter((item) => item.isActive)
    .map((item) => ({
      ...item,
      canHaveServices: item.propertyTypes?.some((p) => p.propertyType === 'service') || false,
    }));

  if (allowedNodeTypes !== undefined) {
    if (!allowedNodeTypes?.length) return [];
    data = data.filter((item) => {
      const checkAllowed = allowedNodeTypes?.includes(item.code);
      const checkPreferred =
        preferedNodeTypes.length > 0
          ? preferedNodeTypes.includes(item.code)
          : isBrandParent
            ? true
            : false;
      return checkAllowed && checkPreferred;
    });
    return data;
  }
  //We need to set prefered types on the top of the list
  else if (preferedNodeTypes) {
    const preferedTypes = data.filter((item) => preferedNodeTypes.includes(item.code));

    if (preferedTypes) {
      const notPreferedTypes = data.filter((item) => !preferedNodeTypes.includes(item.code));

      data = [...preferedTypes, ...notPreferedTypes];
    }
  }

  return data || [];
};

export const getNodeAttributes = (
  structureAddressDataset: Array<AddressDataType>,
  propertyTypes: Array<string>,
  structureContactDataset: Array<ContactDataType>,
  tradingHoursData: Array<TradingHoursData>,
  capacityData: Array<CapacityData>,
  structureLayoutData: StructureLayoutData,
  checkedGlobalReturns: boolean,
  isSiteSchemaEnabled: boolean,
  checkedCentralSupplyWarehouse = false
) => {
  // Heterogeneous attribute payloads; the propertyTypeCode is what identifies each.
  const attributes: Array<Record<string, unknown> & { propertyTypeCode: string }> = [];
  if (structureAddressDataset && structureAddressDataset.length) {
    structureAddressDataset.forEach((address) => {
      attributes.push({
        ...address,
        addressName: address.name,
        name: 'Address',
        propertyTypeCode: PropertyTypeEnum.Address,
      });
    });
  }
  //If address is required by the node type, insert an empty Main record
  else if (propertyTypes.includes(PropertyTypeEnum.Address)) {
    attributes.push({
      ...EmptyMainAddress,
      addressName: EmptyMainAddress.name,
      name: 'Main Address',
      propertyTypeCode: PropertyTypeEnum.Address,
    });
  }

  if (structureContactDataset && structureContactDataset.length > 0) {
    structureContactDataset.forEach((contact) => {
      attributes.push({
        ...contact,
        name: 'Contact',
        propertyTypeCode: PropertyTypeEnum.Contact,
      });
    });
  }
  //If contacts is required by the node type, insert an empty Main record
  else if (propertyTypes.includes(PropertyTypeEnum.Contact)) {
    attributes.push({
      ...EmptyMainContact,
      name: 'Main Contact',
      propertyTypeCode: PropertyTypeEnum.Contact,
    });
  }

  if (tradingHoursData && tradingHoursData.length) {
    tradingHoursData.forEach((tradingHours) => {
      attributes.push({
        ...tradingHours,
        name: 'Trading Hours',
        propertyTypeCode: PropertyTypeEnum.TradingHours,
      });
    });
  }

  if (capacityData && capacityData.length) {
    capacityData.forEach((capacityData) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { itemsData, clientsData, contractsData, ...filteredCapacityData } = capacityData;
      attributes.push({
        ...filteredCapacityData,
        name: 'Capacity',
        propertyTypeCode: PropertyTypeEnum.Allocations,
      });
    });
  }

  if (structureLayoutData) {
    attributes.push({
      id: structureLayoutData.refAttribId,
      referenceNo1: structureLayoutData.referenceNo1,
      referenceNo2: structureLayoutData.referenceNo2,
      referenceNo3: structureLayoutData.referenceNo3,
      name: 'Reference Numbers',
      propertyTypeCode: PropertyTypeEnum.ReferenceNumbers,
    });
  }

  if (isSiteSchemaEnabled) {
    attributes.push({
      id: structureLayoutData.siteSchemaAttribId,
      siteSchemaId: parseInt(structureLayoutData.siteSchemaId),
      name: 'Site Schema',
      propertyTypeCode: PropertyTypeEnum.SiteSchema,
    });
  }

  if (checkedGlobalReturns) {
    attributes.push({
      globalReturns: checkedGlobalReturns,
      name: 'Global Returns',
      propertyTypeCode: PropertyTypeEnum.GlobalReturns,
    });
  }

  // Singleton marker: only one location org-wide may hold it. Omitting the
  // attribute when unchecked is what clears it off this node on save.
  if (checkedCentralSupplyWarehouse) {
    attributes.push({
      centralSupplyWarehouse: checkedCentralSupplyWarehouse,
      name: 'Central Supply Warehouse',
      propertyTypeCode: PropertyTypeEnum.CentralSupplyWarehouse,
    });
  }

  return attributes;
};

export const genarateURL = (
  urlPath: string,
  id?: string,
  structureLayoutName?: string,
  selectedNodeTypeCode?: string,
  selectedStructureType?: string,
  structureTypes?: Array<StructureTypeResponse>,
  clientId?: string,
  contractId?: string,
  brandId?: string
) => {
  const structurePageUrl = getCreateStructurePageUrl(urlPath);
  let url = `${structurePageUrl}?parentId=${id}&parentName=${structureLayoutName}${selectedNodeTypeCode ? `&nodeTypeCode=${selectedNodeTypeCode}` : ''}`;

  if (clientId) {
    url += `&clientId=${clientId}`;
  }
  if (contractId) {
    url += `&contractId=${contractId}`;
  }
  if (brandId) {
    url += `&brandId=${brandId}`;
  }

  if (!selectedNodeTypeCode) {
    const parentNodeType = structureTypes?.find((item) => item.code === selectedStructureType);

    if (
      parentNodeType &&
      parentNodeType.childNodeTypes &&
      parentNodeType.childNodeTypes.length > 0
    ) {
      parentNodeType.childNodeTypes.map((item) => {
        url += `&preferedNodeType=${item.code}`;
      });
    }
  }

  return url;
};

export const getSiteSchemas = (siteSchemas: Array<SiteSchemaDropdown>) => {
  const siteSchemaList = siteSchemas.map((siteSchema) => {
    return {
      value: siteSchema.id.toString(),
      label: siteSchema.name,
      secondaryLabel: siteSchema.description,
    };
  });

  return siteSchemaList;
};

export const generateImportURL = (importSessionID: number) => {
  return `/configure/structure/search?importSessionId=${importSessionID}`;
};

export const getDropdownOptionsWithHandlingAnyKey = (
  selectedValue: Array<string>,
  options: Array<DropdownItem>
) => {
  return selectedValue?.includes(ANY_KEYWORD)
    ? options?.filter((item) => item?.value === ANY_KEYWORD)
    : options;
};

export const configureSelectedDropdownOptions = (selectedValues: string) => {
  return selectedValues.split(',').includes(ANY_KEYWORD)
    ? [ANY_KEYWORD]
    : selectedValues.split(',').filter((selectedValue) => selectedValue);
};

export const getFleetTagOptions = (fleetTagData: Array<StructureAttribute>) => {
  const fleetTags = fleetTagData.reduce((accumulator: DropdownItem[], currentValue) => {
    if (currentValue.value?.isEnabled) {
      const option: DropdownItem = {
        label: currentValue.value?.fleetTagName,
        value: currentValue.id.toString(),
      };
      return [...accumulator, option];
    }
    return accumulator;
  }, []);
  return fleetTags;
};

export const hasErrorInMerge = (
  location: StructureMergeLocationNodeData,
  addressesDecision: string,
  contactsDecision: string,
  tradingHoursDecision: string
) => {
  return (
    (location?.canHaveAddress && isDropDownEmpty(addressesDecision)) ||
    (location?.canHaveContact && isDropDownEmpty(contactsDecision)) ||
    (location?.canHaveTradingHours && isDropDownEmpty(tradingHoursDecision))
  );
};

export const attributesValidation = (value: string, text: string) => {
  return isEmptyString(value) ? text + EMPTY_FIELD_MESSAGE : '';
};

export const getWarningsOnlyForStructures = (warningData: Warning[], byPassContact?: boolean) => {
  if (warningData) {
    //For structure locations we should show warnings related to address and contacts since we allow only those attribute to be added
    return warningData.filter(
      (warning) =>
        warning.field === PropertyTypeEnum.Address ||
        (!byPassContact && warning.field === PropertyTypeEnum.Contact) ||
        warning.field === PropertyTypeEnum.Allocations
    );
  }

  return [];
};

export const getWarningsForAddressContacts = (addressForm: unknown[], contactForm: unknown[]) => {
  if (addressForm.length > 0) {
    return [
      {
        field: 'Address',
        message: 'There are unsaved address/addresses exist. Please save them before proceeding',
      },
    ];
  }

  if (contactForm.length > 0) {
    return [
      {
        field: 'Contact',
        message: 'There are unsaved contact/contacts exist. Please save them before proceeding',
      },
    ];
  }

  return [];
};

export const getServicePresentationDescriptionFromValue = (presentationValue: string) => {
  return SERVICE_PRESENTATION_OPTIONS.find((option) => option.value === presentationValue)?.label;
};

export const addNoneOptionToPresentationValues = () => {
  const presentationOptions = SERVICE_PRESENTATION_OPTIONS;
  presentationOptions.length === 5 && presentationOptions.unshift({ value: '0', label: 'None' });
  return presentationOptions;
};

export const sortStatusTransitionRules = (
  statusTransitionRules: ServiceStatusTransitionsType[]
) => {
  return statusTransitionRules.sort(
    (rule1, rule2) =>
      rule1.selectedServiceType.localeCompare(rule2.selectedServiceType) ||
      rule1.selectedServiceStatus.localeCompare(rule2.selectedServiceStatus)
  );
};

export const validateStatusTransitionRule = (
  statusTransitionRules: ServiceStatusTransitionsType[]
) => {
  let validated = true;
  for (const rule of statusTransitionRules) {
    if (
      !rule.selectedServiceType ||
      !rule.selectedServiceStatus ||
      !rule.selectedPriorities.length ||
      !rule.selectedActionTypes.length ||
      !rule.blockedStatuses.length
    ) {
      validated = false;
    }
  }
  return validated;
};

export const filterAdditionalConfigs = (
  specialServiceTasks: StructureAdditionalConfigOptionType[],
  nodeId: number,
  isContract: boolean
) => {
  return specialServiceTasks.filter((taskType) => {
    if (taskType.value === SpecialServiceTasksEnum.WorkOrderRequest) {
      if (
        nodeId === parseInt(REACT_APP_CVTS_CLIENT_NODE_ID) ||
        nodeId === parseInt(REACT_APP_CVTS_CONTRACT_NODE_ID)
      ) {
        return true;
      } else return false;
    } else if (taskType.value === SpecialServiceTasksEnum.MultiOnSiteSuggestions) {
      return isContract;
    } else return true;
  });
};

export const validateServiceTypeMappings = (serviceTypeMappings: ServiceTypeMappingType[]) => {
  let validated = true;
  for (const serviceTypeMapping of serviceTypeMappings) {
    if (
      !serviceTypeMapping.mappedName ||
      !serviceTypeMapping.selectedServiceType ||
      !serviceTypeMapping.selectedActionType ||
      !serviceTypeMapping.selectedProcessType
    ) {
      validated = false;
    }
  }
  return validated;
};

export const validateRegionOverrides = (regionOverrides: RegionOverridesType[]) => {
  let validated = true;
  for (const regionOverride of regionOverrides) {
    if (!regionOverride.postCode || !regionOverride.suburbName || !regionOverride.regionOverride) {
      validated = false;
    }
  }
  return validated;
};

export const getCreateStructurePageUrl = (urlPath: string) => {
  return urlPath?.includes('configure') ? '/configure/structure/create' : '/structure/create';
};

export const getUpdateStructurePageUrl = (urlPath: string, structureId: number) => {
  return urlPath?.includes('configure')
    ? `/configure/structure/${structureId}`
    : `/structure/${structureId}`;
};
